import type { ThemeId, TimeOfDay, AvatarConfig, WordDTO, ChallengeKind } from "@shared/types";
import type { UpgradeLevels } from "@shared/upgrades";
import type { MovementMode } from "../local/gameSettings";

export interface SpawnedCoin {
  id: number;
  z: number;
  lane: number;
  collected: boolean;
}

export interface WordGate {
  id: number;
  z: number;
  word: WordDTO;
  triggered: boolean;
}

export interface RunState {
  hearts: number;
  stumbles: number;
  coins: number;
  score: number;
  startedAt: number;
  finishedAt: number | null;
  distance: number;
  paused: boolean;
  speed: number;
  invulnerableUntil: number;
}

export interface GameConfig {
  lessonId: string;
  words: WordDTO[];
  theme: ThemeId;
  timeOfDay: TimeOfDay;
  questionsCount: number;
  objectsCount: number;
  coinsCount: number;
  /** Coins awarded for one physical coin pickup. Scales by level. */
  coinPickupValue?: number;
  /** Coins awarded for a correct word gate answer before upgrades. Scales by level. */
  quizCorrectCoins?: number;
  courseLength: number; // world units
  seed: number;
  avatar: AvatarConfig;
  /** Restrict gates to these challenge kinds. Null/empty = full rotation. */
  challengeKinds?: ChallengeKind[] | null;
  /** Persistent upgrade levels purchased by this student. */
  upgrades?: UpgradeLevels;
  /** Fixed run speed in m/s. Overrides the default ramp. */
  runSpeed?: number;
  /** Free movement is the default Fall Guys-style mode; lanes remain for beginners. */
  movementMode?: MovementMode;
  /** Gamepad/analog movement sensitivity. Keyboard stays digital. */
  movementSensitivity?: number;
  /** Right-stick camera sensitivity. */
  cameraSensitivity?: number;
  /** Controller stick deadzone. */
  controllerDeadzone?: number;
  /** Multiplayer: opponents to render as ghosts on the same path. */
  remotePlayers?: { id: string; displayName: string; avatarConfig: AvatarConfig; slotIdx: number }[];
  /** Multiplayer: after local death, follow a remaining live opponent until results. */
  spectateAfterDeath?: boolean;
  /** Multiplayer: my own slot index, used to compute lateral offset. */
  mySlotIdx?: number;
  /** Mount the game in paused state (for showing a countdown overlay first). */
  startPaused?: boolean;
  /** When mounted paused at the start, preview the track from finish to start. */
  startCameraFlyover?: boolean;
}

export interface RunOutcome {
  score: number;
  timeMs: number;
  coinsEarned: number; // raw coins picked up + answer rewards (excludes bonuses below)
  hearts: number;
  stumbles: number;
  wordOutcomes: { wordId: string; correct: boolean }[];
  finished: boolean; // reached the end vs. failed the learning checks
}

export interface BonusBreakdown {
  baseCoins: number;
  heartsBonus: number;
  timeBonus: number;
  flawlessBonus: number;
  totalCoins: number;
  totalScore: number;
  flawless: boolean;
}

export function computeBonuses(o: RunOutcome): BonusBreakdown {
  const total = o.wordOutcomes.length;
  const correct = o.wordOutcomes.filter((w) => w.correct).length;
  const allCorrect = total > 0 && correct === total;
  const heartsBonus = o.finished ? o.hearts * 25 : 0;
  // Time bonus: faster than 60s = up to +50, scaled.
  const seconds = o.timeMs / 1000;
  const timeBonus = o.finished ? Math.max(0, Math.round((90 - seconds) * 1.5)) : 0;
  const flawless = o.finished && allCorrect && o.hearts === 3;
  const flawlessBonus = flawless ? 100 : 0;
  return {
    baseCoins: o.coinsEarned,
    heartsBonus,
    timeBonus,
    flawlessBonus,
    totalCoins: o.coinsEarned + heartsBonus + timeBonus + flawlessBonus,
    totalScore: o.score + heartsBonus * 5 + timeBonus * 5 + flawlessBonus * 5,
    flawless,
  };
}

export function rewardSettingsForLesson(orderIndex: number, baseCoinsCount: number) {
  const level = Math.max(1, orderIndex);
  return {
    coinsCount: baseCoinsCount + (level - 1) * 8,
    coinPickupValue: 1 + Math.floor((level - 1) / 3),
    quizCorrectCoins: 5 + Math.floor((level - 1) * 1.5),
  };
}

export type GameEvent =
  | { type: "challenge"; word: WordDTO; resolve: (correct: boolean) => void }
  | { type: "hit" }
  | { type: "coin"; value: number }
  | { type: "finish"; outcome: RunOutcome }
  | { type: "death"; outcome: RunOutcome }
  | { type: "state"; state: RunState };

export type GameEventHandler = (e: GameEvent) => void;
